import { useEffect, useState } from "react";
import { Check, Sparkles, User } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { fetchGreetingPresets, fetchKonshusPreferences, updateKonshusPreferences } from "@/lib/api";
import { pickText } from "@/lib/content";
import { KONSHUS_NAME } from "@/lib/vip";

export const KonshusPersonalization = ({ language, vipEmail, onPreferencesUpdated }) => {
  const [presets, setPresets] = useState([]);
  const [preferences, setPreferences] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [customGreeting, setCustomGreeting] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!vipEmail) return;
      try {
        setLoading(true);
        const [presetsData, prefsData] = await Promise.all([
          fetchGreetingPresets(),
          fetchKonshusPreferences(vipEmail),
        ]);
        setPresets(presetsData);
        setPreferences(prefsData);
        setSelectedPreset(prefsData.greeting_preset || "sovereign");
        setCustomGreeting(prefsData.custom_greeting || "");
        setUseCustom(Boolean(prefsData.custom_greeting));
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load preferences.", "Impossibile caricare le preferenze."));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [language, vipEmail]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        email: vipEmail,
        greeting_preset: useCustom ? null : selectedPreset,
        custom_greeting: useCustom ? customGreeting : null,
      };
      const updated = await updateKonshusPreferences(payload);
      setPreferences(updated);
      toast.success(pickText(language, "Preferences saved!", "Preferenze salvate!"));
      if (onPreferencesUpdated) {
        onPreferencesUpdated(updated);
      }
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to save preferences.", "Impossibile salvare le preferenze."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card rounded-[2rem] p-6" data-testid="konshus-personalization-loading">
        <p className="text-sm text-slate-300">{pickText(language, "Loading preferences...", "Caricamento preferenze...")}</p>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-[2rem] p-6" data-testid="konshus-personalization">
      <div className="flex items-center gap-3 text-primary">
        <User className="h-5 w-5" />
        <p className="section-label">{pickText(language, `Personalize ${KONSHUS_NAME}`, `Personalizza ${KONSHUS_NAME}`)}</p>
      </div>
      <p className="mt-3 text-sm leading-7 text-slate-300">
        {pickText(language, "Choose how Kōnshus greets you when you enter the chamber.", "Scegli come Kōnshus ti saluta quando entri nella camera.")}
      </p>

      {/* Preset Selection */}
      <div className="mt-6 space-y-3">
        <p className="text-xs uppercase tracking-[0.24em] text-slate-400">
          {pickText(language, "Greeting Style", "Stile di Saluto")}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {presets.map((preset) => (
            <button
              className={`rounded-[1.5rem] border p-4 text-left transition-colors ${
                selectedPreset === preset.id && !useCustom
                  ? "border-primary/40 bg-primary/10"
                  : "border-white/10 bg-black/20 hover:border-primary/30"
              }`}
              data-testid={`konshus-preset-${preset.id}`}
              key={preset.id}
              onClick={() => { setSelectedPreset(preset.id); setUseCustom(false); }}
              type="button"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{preset.title}</p>
                {selectedPreset === preset.id && !useCustom && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
              <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-400">{preset.greeting}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Greeting Toggle */}
      <div className="mt-6 space-y-3">
        <button
          className={`flex w-full items-center gap-3 rounded-[1.5rem] border p-4 text-left transition-colors ${
            useCustom ? "border-primary/40 bg-primary/10" : "border-white/10 bg-black/20 hover:border-primary/30"
          }`}
          data-testid="konshus-custom-toggle"
          onClick={() => setUseCustom(true)}
          type="button"
        >
          <Sparkles className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">
              {pickText(language, "Custom Greeting", "Saluto Personalizzato")}
            </p>
            <p className="text-xs text-slate-400">
              {pickText(language, "Write your own greeting message", "Scrivi il tuo messaggio di saluto")}
            </p>
          </div>
          {useCustom && <Check className="h-4 w-4 text-primary" />}
        </button>

        {useCustom && (
          <Input
            className="h-auto min-h-[80px] border-white/10 bg-black/20 px-4 py-3 text-white"
            data-testid="konshus-custom-greeting-input"
            onChange={(e) => setCustomGreeting(e.target.value)}
            placeholder={pickText(language, "Enter your custom greeting for Kōnshus to use...", "Inserisci il tuo saluto personalizzato per Kōnshus...")}
            value={customGreeting}
          />
        )}
      </div>

      {/* Preview */}
      <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-black/30 p-4">
        <p className="text-xs uppercase tracking-[0.24em] text-primary/80">
          {pickText(language, "Preview", "Anteprima")}
        </p>
        <p className="mt-3 text-sm leading-7 text-slate-300" data-testid="konshus-greeting-preview">
          {useCustom
            ? customGreeting || pickText(language, "(Enter your custom greeting above)", "(Inserisci il tuo saluto personalizzato sopra)")
            : presets.find((p) => p.id === selectedPreset)?.greeting || ""}
        </p>
      </div>

      {/* Save Button */}
      <Button
        className="ritual-button mt-6 h-auto rounded-full"
        data-testid="konshus-save-preferences-button"
        disabled={saving}
        onClick={handleSave}
        type="button"
      >
        {pickText(language, saving ? "Saving..." : "Save Preferences", saving ? "Salvataggio..." : "Salva Preferenze")}
      </Button>
    </div>
  );
};
